import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getAbsenceByStudentId, downloadStatement } from '@webapps/business';
import { useQuery, useMutation } from '@webapps/reactjs-tech-components';
import AvatarCompany from 'components/Avatar/AvatarCompany';

const styles = {
  root: {
    display: 'none',
    padding: '24px',
    marginBottom: '16px',
  },
  header: {
    marginBottom: '16px',
  },
  schoolInfo: {
    marginBottom: '32px',
  },
  table: {
    marginTop: '16px',
    width: '100%',
    // backgroundColor: "red"
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
  },
  avatarContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  tableCell: {
    // padding: '16px',
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
    fontSize: '13px',
    paddingTop: '16px',
    paddingBottom: '16px',
    textAlign: 'start',
    marginLeft: '5px'

    // width: 'calc(20% - 32px)',
  },
  footerContainer: {
    marginTop: '40px',
    textAlign: 'right',
  },
  contentContainer: {
    marginBottom: '24px',
  },
  text13: {
    fontSize: '13px',
  },
  title: {
    fontWeight: 'bold',
    marginBottom: '6px'
  }

};

function formatHoursAndMinutes(hoursDecimal) {
  const heures = Math.floor(hoursDecimal);
  const minutes = Math.round((hoursDecimal - heures) * 60);
  let result = '';

  if (heures > 0 && minutes > 0) {
    result = `${heures}h${minutes.toString().padStart(2, '0')}`;
  } else if (heures > 0) {
    result = `${heures}h`;
  } else if (minutes > 0) {
    result = `${minutes}mn`;
  } else {
    result = '0h';
  }

  return result;
}

const ManyStudentStatement = forwardRef(({ student, startDate, endDate, setHtmls }, ref) => {
  const currentDate = format(new Date(), 'dd MMMM yyyy', { locale: fr });
  const contentRef = useRef(null);
  const [isLoadingAbsences, setIsLoadingAbsences] = useState(true);
  const [isLoadingImage, setIsLoadingImage] = useState(true);
  const loadingStateRef = useRef({ absences: true, image: true });


  const {
    data: absences = [],
    status,
    error
  } = useQuery(getAbsenceByStudentId, {
    key: ['absencesStatement', student?.id],
    variables: { id: student?.id, startDate, endDate },
    enabled: !!student?.id,
    retry: false,
    onSuccess: (data) => {
      setIsLoadingAbsences(false);
      loadingStateRef.current.absences = false;
    },
    onError: (err) => {
      console.error("Error loading absences:", err);
      setIsLoadingAbsences(false);
      loadingStateRef.current.absences = false;
    }
  });

  const handleImageLoad = () => {
    setIsLoadingImage(false);
    loadingStateRef.current.image = false;
  };

  const handleImageError = () => {
    setIsLoadingImage(false);
    loadingStateRef.current.image = false;
  };

  useEffect(() => {
    loadingStateRef.current.absences = isLoadingAbsences;
    loadingStateRef.current.image = isLoadingImage;

    // Only update HTML content when everything is loaded
    if (!isLoadingAbsences && !isLoadingImage && contentRef.current?.outerHTML) {
      // Check if this student's HTML is already in the array
      setHtmls(prevHtmls => {
        const existingIndex = prevHtmls.findIndex(html => html.studentId === student.id);

        if (existingIndex !== -1) {
          // Update existing HTML
          const updatedHtmls = [...prevHtmls];
          updatedHtmls[existingIndex] = {
            studentId: student.id,
            studentFirstName: student.firstname,
            studentLastName: student.lastname,
            html: contentRef.current.outerHTML
          };
          return updatedHtmls;
        } else {
          // Add new HTML
          return [...prevHtmls, {
            studentId: student.id,
            studentFirstName: student.firstname,
            studentLastName: student.lastname,
            html: contentRef.current.outerHTML
          }];
        }
      });
    }
  }, [isLoadingAbsences, isLoadingImage, absences, student.id, setHtmls]);

  useEffect(() => {
    if (student?.id) {
      setIsLoadingImage(true);
      loadingStateRef.current.image = true;
      setIsLoadingAbsences(true);
      loadingStateRef.current.absences = true;
    }
  }, [student?.id]);

  useEffect(() => {
    if (isLoadingImage) {
      const timer = setTimeout(() => {
        setIsLoadingImage(false);
        loadingStateRef.current.image = false;
      }, 2000); // 2 seconds timeout
      return () => clearTimeout(timer);
    }
  }, [isLoadingImage]);


  useEffect(() => {
    setIsLoadingAbsences(true);
    loadingStateRef.current.absences = true;
  }, [startDate, endDate]);

  const isLoading = () => loadingStateRef.current.absences || loadingStateRef.current.image;

  useImperativeHandle(ref, () => ({
    generatePdf: async () => {

      // Check if all resources are loaded
      const isLoading = () => {
        return loadingStateRef.current.absences || loadingStateRef.current.image;
      };

      // Function to download the PDF
      const downloadPdf = async () => {
        if (!contentRef.current) {
          console.error("Content ref is null");
          return null;
        }

        try {
          const element = contentRef.current;

          // Create a new jsPDF instance
          const { jsPDF } = await import('jspdf');
          const { default: html2canvas } = await import('html2canvas');

          const canvas = await html2canvas(element, {
            scale: 2,
            useCORS: true,
            logging: true
          });

          const imgData = canvas.toDataURL('image/png');
          const pdf = new jsPDF('p', 'mm', 'a4');
          const imgProps = pdf.getImageProperties(imgData);
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

          pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

          // Return the PDF as a blob
          const blob = pdf.output('blob');
          return blob;
        } catch (error) {
          console.error("Error generating PDF:", error);
          return null;
        }
      };

      // If resources are still loading, wait for them to complete
      if (isLoading()) {
        return new Promise((resolve) => {
          const checkLoading = setInterval(() => {
            if (!isLoading()) {
              clearInterval(checkLoading);
              downloadPdf().then(resolve);
            }
          }, 500);

          // Safety timeout after 10 seconds
          setTimeout(() => {
            clearInterval(checkLoading);
            downloadPdf().then(resolve);
          }, 10000);
        });
      } else {
        return downloadPdf();
      }
    }
  }), [student, absences, startDate, endDate]);

  const getSchoolCity = () => {
    return student?.classroom?.school?.address?.city;
  }

  return (
    <Paper style={styles.root} ref={contentRef} id="hidden-content">
      <div style={styles.header}>
        <div style={styles.avatarContainer}>
          <AvatarCompany
            height={80}
            idCompany={student?.classroom?.school?.id}
            name={student?.classroom?.school?.name}
            width={80}
            onLoad={handleImageLoad}
            onError={handleImageError}
            toHtml={true}
          />
        </div>
        <div style={styles.schoolInfo}>
          <Typography>{student?.firstname} {student?.lastname}</Typography>
          <Typography>{student?.address?.label}</Typography>
          <Typography>{student?.address?.cityCode} {student?.address?.city}</Typography>
        </div>
        <Typography>{getSchoolCity()} le {currentDate}</Typography>
      </div>

      <div style={styles.contentContainer}>
        <Typography style={styles.title}>
          {/* Relevé d'absences et de retard du {format(new Date(startDate), 'dd/MM/yyyy')} au {format(new Date(endDate), 'dd/MM/yyyy')} */}
        </Typography>

        <Typography style={styles.text13}>
          Vous trouverez ci-dessous le récapitulatif des absences et des retards de l'{student?.gender === "MALE" ? "étudiant" : student?.gender === "FEMALE" ? "étudiante" : "étudiant(e)"} {student?.firstname} {student?.lastname} en {student?.classroom?.label}
        </Typography>

        <Typography style={styles.text13}>
          Nous vous rappelons que toute absence ou tout retard doit être justifié auprès du secrétariat de l'accueil
        </Typography>
      </div>

      <TableContainer style={styles.table}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ ...styles.tableCell, ...styles.tableHeader, ...styles.text13, width: '20%' }}>Type</TableCell>
              <TableCell style={{ ...styles.tableCell, ...styles.tableHeader, ...styles.text13, width: '20%' }}>Date</TableCell>
              <TableCell style={{ ...styles.tableCell, ...styles.tableHeader, ...styles.text13, width: '20%' }}>Description</TableCell>
              <TableCell style={{ ...styles.tableCell, ...styles.tableHeader, ...styles.text13, width: '20%' }}>Temps</TableCell>
              <TableCell style={{ ...styles.tableCell, ...styles.tableHeader, ...styles.text13, width: '20%' }}>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {absences.sort((a, b) => new Date(a.startDate) - new Date(b.startDate)).map((absence, index) => (
              <TableRow key={index}>
                <TableCell style={styles.tableCell}>Absence</TableCell>
                <TableCell style={styles.tableCell}>
                  {/* {format(new Date(absence.startDate), 'EEE dd/MM/yy', { locale: fr })} - {format(new Date(absence.endDate), 'EEE dd/MM/yy', { locale: fr })} */}
                </TableCell>
                <TableCell style={styles.tableCell}>{absence.reason}</TableCell>
                <TableCell style={styles.tableCell}>{formatHoursAndMinutes(absence?.totalHours)}</TableCell>
                <TableCell style={styles.tableCell}>{absence?.absenceLocation === "COMPANY" ? "En entreprise" : "À l'école"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <div style={styles.footerContainer}>
        <Typography>{student?.classroom?.school?.name}</Typography>
      </div>
    </Paper>
  );
});

export default ManyStudentStatement;